import React from 'react';
import Icon3D from './Icon3D';

/**
 * The parts of a listing the app shows and the website did not.
 *
 * <p>The search and detail endpoints already send `amenties`, `nearbyPlaces`,
 * `lifestyleMatchScore`, `lifestyleMatch` and `payingGuest` to both clients.
 * The app renders all of them; the website was dropping them on the floor, so
 * the same listing looked materially thinner on the web than on a phone.
 *
 * <p>Every table below mirrors a constant in dofast-ui: AMENITY_3D and
 * PLACE_CATEGORY_3D from src/constants/api.js, CATEGORY_TINT from
 * components/NearbyPlaces.js, and the score bands from
 * screens/listing/ListingDetailScreen.js. Keys are the API's enum names, so a
 * new category added on the backend needs one line here and one PNG — the same
 * two edits it needs in the app.
 */

/** Amenity enum → icon slot. Matches AMENITY_3D. FRIDEG is the API's spelling. */
export const AMENITY_3D = {
    TV: 'tv',
    FRIDEG: 'fridge',
    KITCHEN: 'kitchen',
    WIFI: 'wifi',
    MACHINE: 'washing-machine',
    AC: 'ac',
    POWER_BACKUP: 'power-backup',
    COOK: 'cook',
    PARKING: 'parking',
};

export const AMENITY_LABELS = {
    TV: 'TV',
    FRIDEG: 'Fridge',
    KITCHEN: 'Kitchen',
    WIFI: 'Wi-Fi',
    MACHINE: 'Washing Machine',
    AC: 'AC',
    POWER_BACKUP: 'Power Backup',
    COOK: 'Cook',
    PARKING: 'Parking',
};

/** PlaceCategory enum → icon slot. Matches PLACE_CATEGORY_3D. */
export const PLACE_CATEGORY_3D = {
    GROCERY: 'grocery',
    METRO_STATION: 'metro',
    HOSPITAL: 'hospital',
    PHARMACY: 'pharmacy',
    BUS_STOP: 'bus-stop',
    GYM: 'gym',
    RESTAURANT: 'restaurant',
    ATM: 'atm',
};

const CATEGORY_TINT = {
    GROCERY: '#DCFCE7',
    METRO_STATION: '#DBEAFE',
    HOSPITAL: '#FEE2E2',
    PHARMACY: '#CCFBF1',
    BUS_STOP: '#FEF3C7',
    GYM: '#EDE9FE',
    RESTAURANT: '#FFEDD5',
    ATM: '#E0E7FF',
};

const tintFor = (c) => CATEGORY_TINT[c] || '#EEF2F7';

/** Nearest first, so a truncated row always shows the most relevant places. */
const byDistance = (a, b) => (a.distanceMeters ?? Infinity) - (b.distanceMeters ?? Infinity);

/** Metres → a short human distance. */
export const formatDistance = (m) =>
    m == null ? '' : m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;

/**
 * Google Maps URL for a place. Sends the name alongside the coordinates rather
 * than coordinates alone, so Maps drops a labelled pin on the actual business
 * instead of an anonymous point — same reasoning as the app's openPlaceInMaps.
 */
export const mapsUrl = (place) => {
    const hasCoords = place?.latitude != null && place?.longitude != null;
    const name = place?.name || place?.label || '';
    if (hasCoords && name) {
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}&center=${place.latitude},${place.longitude}`;
    }
    if (hasCoords) return `https://www.google.com/maps/search/?api=1&query=${place.latitude},${place.longitude}`;
    if (name) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}`;
    return null;
};

/** The 75 / 50 bands the app's detail screen uses, so a score reads the same. */
export const matchColor = (score) =>
    score >= 75 ? '#7C3AED' : score >= 50 ? '#A78BFA' : '#94A3B8';

export const matchCaption = (score) =>
    score >= 75 ? 'You two are highly compatible'
        : score >= 50 ? 'Fairly compatible lifestyles'
            : 'Some lifestyle differences';

/* ── Card-sized pieces ─────────────────────────────────────────────────────── */

/** Amenity icons on a listing card. Five, then a +N chip, as in the app. */
export const AmenityIconRow = ({ amenities, max = 5, style }) => {
    const list = amenities || [];
    if (list.length === 0) return null;
    const shown = list.slice(0, max);
    const extra = list.length - shown.length;

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', ...style }}>
            {shown.map((a) => (
                <span
                    key={a}
                    title={AMENITY_LABELS[a] || a}
                    style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#F3F5F8', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                >
                    <Icon3D name={AMENITY_3D[a] || 'verified'} size={18} label={AMENITY_LABELS[a] || a} />
                </span>
            ))}
            {extra > 0 && (
                <span style={{ height: '28px', padding: '0 9px', borderRadius: '14px', background: '#F3F5F8', display: 'inline-flex', alignItems: 'center', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)' }}>
                    +{extra}
                </span>
            )}
        </div>
    );
};

/** Nearby-place icons on a listing card, nearest first. */
export const NearbyIconRow = ({ places, max = 4, style }) => {
    const sorted = [...(places || [])].sort(byDistance);
    if (sorted.length === 0) return null;
    const shown = sorted.slice(0, max);
    const extra = sorted.length - shown.length;

    return (
        <div
            style={{ display: 'flex', alignItems: 'center', gap: '6px', ...style }}
            title={sorted.map((p) => `${p.label} · ${formatDistance(p.distanceMeters)}`).join('\n')}
            aria-label={`${sorted.length} places nearby`}
        >
            {shown.map((p, i) => (
                <span
                    key={`${p.category}-${i}`}
                    style={{ width: '28px', height: '28px', borderRadius: '50%', background: tintFor(p.category), display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                >
                    <Icon3D name={PLACE_CATEGORY_3D[p.category] || 'location'} size={18} label={p.label} />
                </span>
            ))}
            {extra > 0 && (
                <span style={{ height: '28px', padding: '0 9px', borderRadius: '14px', background: '#F3F5F8', display: 'inline-flex', alignItems: 'center', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)' }}>
                    +{extra}
                </span>
            )}
        </div>
    );
};

/** Compact lifestyle-match chip that rides on the card's location line. */
export const MatchChip = ({ score, style }) => {
    if (score == null) return null;
    const c = matchColor(score);
    return (
        <span
            title={matchCaption(Math.round(score))}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '12px', background: `${c}1A`, color: c, fontSize: '12px', fontWeight: 800, flexShrink: 0, ...style }}
        >
            {Math.round(score)}% match
        </span>
    );
};

/** Per-occupancy PG prices. Only rows the owner actually filled in. */
export const PgPriceRow = ({ payingGuest, style }) => {
    if (!payingGuest) return null;
    const rows = [
        ['1', payingGuest.singleSharingPrice],
        ['2', payingGuest.doubleSharingPrice],
        ['3', payingGuest.tripleSharingPrice],
    ].filter(([, v]) => v > 0);
    if (rows.length === 0) return null;

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', ...style }}>
            {rows.map(([n, v]) => (
                <span key={n} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '12px', background: 'var(--tag-pg)', color: 'var(--tag-pg-ink)', fontSize: '12px', fontWeight: 700 }}>
                    <Icon3D name="rooms" size={14} /> {n}-share ₹{v.toLocaleString('en-IN')}
                </span>
            ))}
        </div>
    );
};

/** RENTED / EXPIRED / BLOCKED. Nothing renders for an active listing. */
export const StatusBadge = ({ status, style }) => {
    if (!status || status === 'ACTIVE') return null;
    const colors = {
        RENTED: { bg: '#D1FAE5', text: '#059669' },
        EXPIRED: { bg: '#FEF3C7', text: '#D97706' },
        BLOCKED: { bg: '#FEE2E2', text: '#DC2626' },
        DELETED: { bg: '#FEE2E2', text: '#DC2626' },
    }[status] || { bg: '#FEF3C7', text: '#D97706' };

    return (
        <span style={{ padding: '4px 10px', borderRadius: '10px', background: colors.bg, color: colors.text, fontSize: '11px', fontWeight: 800, letterSpacing: '0.4px', ...style }}>
            {status}
        </span>
    );
};

/* ── Detail-page pieces ────────────────────────────────────────────────────── */

/**
 * Nearby places as named tiles with distances, each opening the place in Maps.
 *
 * <p>The card shows icons only because it has no room for nine names; here the
 * visitor has committed to the listing and wants the specifics, which is the
 * same split the app makes between NearbyIconRow and NearbyPlaceGrid.
 *
 * <p>Sized off the app's tile: a 64px tinted disc carrying a 42px icon. The
 * first web version drew these at 26px, which turned artwork with real depth
 * and shading into indistinct specks — a 3D icon set stops reading as 3D much
 * below about 32px.
 *
 * <p>Navigation is same-tab by request. Worth knowing what that trades away: a
 * visitor who opens a place leaves the listing behind, and getting back is a
 * browser Back press rather than closing a tab.
 */
export const NearbyPlaceGrid = ({ places }) => {
    const list = [...(places || [])].sort(byDistance);
    if (list.length === 0) return null;

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '14px' }}>
            {list.map((p, i) => {
                const href = mapsUrl(p);
                const Tag = href ? 'a' : 'div';
                return (
                    <Tag
                        key={`${p.category}-${i}`}
                        {...(href ? { href } : {})}
                        title={href ? `Open ${p.label} in Google Maps` : undefined}
                        style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            gap: '4px', padding: '18px 12px 16px',
                            borderRadius: '20px', background: 'white',
                            border: '1px solid var(--border-glass)',
                            color: 'inherit', textDecoration: 'none',
                            transition: 'transform 0.18s, box-shadow 0.18s, border-color 0.18s',
                            cursor: href ? 'pointer' : 'default',
                        }}
                        onMouseOver={(e) => {
                            if (!href) return;
                            e.currentTarget.style.transform = 'translateY(-3px)';
                            e.currentTarget.style.boxShadow = 'var(--shadow-float)';
                            e.currentTarget.style.borderColor = 'var(--brand-primary)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.borderColor = 'var(--border-glass)';
                        }}
                    >
                        <span style={{ width: '64px', height: '64px', borderRadius: '50%', background: tintFor(p.category), display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginBottom: '6px' }}>
                            <Icon3D name={PLACE_CATEGORY_3D[p.category] || 'location'} size={42} />
                        </span>
                        <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: '13.5px', fontWeight: 700, color: 'var(--text-dark)', lineHeight: '1.3', textAlign: 'center' }}>
                            {p.label}
                        </span>
                        {/* marginTop:auto pins the distance to the tile's floor, so
                            a two-line name like "Chinmaya Mission Hospital" does not
                            shove its distance out of line with the rest of the row. */}
                        <span style={{ marginTop: 'auto', paddingTop: '4px', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700, letterSpacing: '0.2px' }}>
                            {formatDistance(p.distanceMeters)}
                        </span>
                    </Tag>
                );
            })}
        </div>
    );
};

/** SVG equivalent of the app's ProgressRing. */
const Ring = ({ score, size = 88, thickness = 8 }) => {
    const r = (size - thickness) / 2;
    const circ = 2 * Math.PI * r;
    const c = matchColor(score);
    return (
        <svg width={size} height={size} style={{ flexShrink: 0 }} aria-hidden="true">
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EEF0F3" strokeWidth={thickness} />
            <circle
                cx={size / 2} cy={size / 2} r={r} fill="none" stroke={c} strokeWidth={thickness}
                strokeDasharray={`${(circ * score) / 100} ${circ}`} strokeLinecap="round"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central"
                fontSize={size * 0.26} fontWeight="800" fill={c}>{Math.round(score)}%</text>
        </svg>
    );
};

/** Lifestyle match with the shared/differing preference chips. */
export const MatchCard = ({ score, match }) => {
    if (score == null) return null;
    const rounded = Math.round(score);

    return (
        <div style={{ background: 'white', borderRadius: '24px', border: '1px solid var(--border-glass)', padding: '26px', marginBottom: '20px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <Ring score={rounded} />
                <div>
                    <h3 style={{ fontSize: '19px', fontWeight: 800, color: 'var(--text-dark)', margin: 0, letterSpacing: '-0.3px' }}>Lifestyle match</h3>
                    <p style={{ fontSize: '14.5px', color: 'var(--text-muted)', margin: '5px 0 0', lineHeight: 1.5 }}>{matchCaption(rounded)}</p>
                </div>
            </div>

            {match?.shared?.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>You both prefer</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {match.shared.map((t) => (
                            <span key={`s-${t}`} style={{ padding: '7px 14px', borderRadius: '14px', background: '#D1FAE5', color: '#047857', fontSize: '13.5px', fontWeight: 700 }}>{t}</span>
                        ))}
                    </div>
                </div>
            )}

            {match?.conflicts?.length > 0 && (
                <div style={{ marginTop: '14px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>Differs on</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {match.conflicts.map((t) => (
                            <span key={`c-${t}`} style={{ padding: '7px 14px', borderRadius: '14px', background: '#FEF3C7', color: '#B45309', fontSize: '13.5px', fontWeight: 700 }}>{t}</span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

/** Amenities as the app draws them on its detail screen: a 44px icon per tile. */
export const AmenityGrid = ({ amenities }) => {
    const list = amenities || [];
    if (list.length === 0) return null;
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
            {list.map((a) => (
                <span
                    key={a}
                    style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                        padding: '18px 10px 14px', borderRadius: '20px',
                        background: 'white', border: '1px solid var(--border-glass)',
                    }}
                >
                    <Icon3D name={AMENITY_3D[a] || 'verified'} size={44} />
                    <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-dark)', textAlign: 'center', lineHeight: '1.3' }}>
                        {AMENITY_LABELS[a] || a}
                    </span>
                </span>
            ))}
        </div>
    );
};
